import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  EmptyState,
  Icon,
  IconTile,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  NavPane,
  NavPaneContent,
  NavPaneEmpty,
  NavPaneGroup,
  NavPaneGroupLabel,
  NavPaneHeader,
  NavPaneItem,
  NavPaneSeparator,
  NavPaneTitle,
  ScrollArea,
  Skeleton,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useElementSize,
  useMediaQuery,
  useIsMobile,
  type Color,
  type IconName,
} from "@salestrak/ui";

import { useIsClipped } from "#app/components/clipped-readout-text";
import { ManageDialogHeader } from "#app/components/manage-dialog-header";
import { ConnectionConfigView, NEW_CONNECTION } from "./connection-config-view";
import {
  ConfigCrumb,
  type ConfigCrumbSegment,
  IconChip,
} from "./integrations-config-primitives";
import {
  filterProviders,
  type IntegrationCategory,
  type IntegrationProvider,
} from "./integrations-data";
import { useIntegrationCatalog } from "./use-integration-catalog";

const DEFAULT_CATEGORY_ID = "featured";
/** The nav pane narrows once the window dialog itself is squeezed: below
 *  `md` the dialog is under ~700px wide, and a 240px nav beside it would
 *  leave the card grid a single column with the provider names breaking
 *  mid-word. */
const NAV_WIDTH = 240;
const NAV_WIDTH_NARROW = 200;
/** The header search: as wide as the row leaves it, capped at the standard
 *  300, never squeezed under a readable field. Past the floor the crumb
 *  beside it truncates instead. 128 is the floor that still leaves the
 *  longest category label whole at the medium tier's narrowest window
 *  (a 640px viewport: 544px dialog, 200px nav, 24px close). */
const SEARCH_MAX_WIDTH = 300;
const SEARCH_MIN_WIDTH = 128;
/** The pressable-tile chrome the Add-Widget gallery's tiles wear — button
 *  text-alignment reset plus the hover border brighten (Box carries no
 *  hover-border prop; recurring across both manage dialogs, a sweep
 *  candidate for one library recipe). */
const CARD_CHROME = "text-start transition-colors hover:border-foreground/32";
// The content's horizontal padding (paddingX="lg") — config-column math.
const CONTENT_PAD_X = 24;
// Centered config-panel column width (header + tabs + option rows).
const CONFIG_MAX_WIDTH = 760;

// Semantic text color for a status label; undefined → muted.
type StatusColor = "success" | "info" | "warning";

// View-model the card renders from.
// The whole card is clickable (opens the config panel); there's no separate
// action button.
type CardModel = {
  id: string;
  icon: IconName;
  /** Icon tint (provider accent color); undefined → muted. */
  iconColor?: Color | undefined;
  /** Full-color brand logo tile; renders instead of `icon` when present. */
  logo?: string | undefined;
  name: string;
  description: string;
  /** Absent = nothing set up — the card carries NO status text. Everything
   *  in the marketplace is available by definition, so a label saying so is
   *  bloat; the slot is reserved for statements that differentiate (live
   *  connection states today; editorial chips like "New" if they come). */
  status?: { label: string; color: StatusColor } | undefined;
  /** Whole-card click → open the config panel. */
  onClick?: (() => void) | undefined;
};

/**
 * The card's status line: what this provider's connections are DOING, not just
 * how many there are.
 *
 * A bare count answered the wrong question. Three connections reads as healthy
 * whether they are three working ones, two switched off, or one whose app was
 * uninstalled this morning — and the last is the case someone needs to act on,
 * which is exactly the one a total hid.
 *
 * Only non-zero counts render, so a healthy provider still reads as one short
 * phrase rather than a row of zeros. No connections at all is NO status —
 * every provider in the marketplace is available by definition, so saying
 * "Available" on each card was a row of identical labels differentiating
 * nothing.
 *
 * Exported for its unit test: this is the only place the states are counted.
 */
export function connectionStatus(connections: IntegrationProvider["connections"]): {
  label: string;
  color: StatusColor;
} | null {
  if (connections.length === 0) return null;

  const attention = connections.filter((c) => c.needsAttention).length;
  const disabled = connections.filter((c) => !c.active && !c.needsAttention).length;
  // Anything neither broken nor switched off. Derived by subtraction rather than
  // filtered again, so the three counts always sum to the total and no
  // connection can be reported twice or not at all.
  const active = connections.length - attention - disabled;

  const parts: string[] = [];
  if (active > 0) parts.push(`${active} active`);
  if (disabled > 0) parts.push(`${disabled} disabled`);
  if (attention > 0) parts.push(`${attention} needs attention`);

  return {
    // Worst state wins the colour: one broken connection among nine healthy ones
    // is the thing worth looking at, so the card must not read as green.
    color: attention > 0 ? "warning" : active > 0 ? "success" : "info",
    label: parts.join(" · "),
  };
}

// Multi-config: the card's status reflects a provider's saved connections.
// Clicking the card opens the config panel.
function providerCard(
  p: IntegrationProvider,
  onOpen?: (p: IntegrationProvider) => void,
): CardModel {
  return {
    id: p.id,
    icon: p.icon,
    iconColor: p.color,
    logo: p.logo,
    name: p.name,
    description: p.description,
    status: connectionStatus(p.connections) ?? undefined,
    onClick: onOpen ? () => onOpen(p) : undefined,
  };
}

function matchesQuery(card: CardModel, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    card.name.toLowerCase().includes(q) ||
    card.description.toLowerCase().includes(q)
  );
}

/** The card's two-line description. Only a CLIPPED one earns the full-text
 * tooltip — an unclipped description already says everything, and a tooltip
 * there would just repeat it. */
function CardDescription({ text }: { text: string }) {
  const { setRef, clipped } = useIsClipped(text);
  const body = (
    <Box ref={setRef}>
      <Text size="sm" truncate={2} variant="muted">
        {text}
      </Text>
    </Box>
  );
  if (!clipped) return body;
  return (
    <Tooltip>
      <TooltipTrigger render={body} />
      <TooltipPopup side="bottom">
        <Text size="xs">{text}</Text>
      </TooltipPopup>
    </Tooltip>
  );
}

/** One marketplace card: the gallery-tile chrome — a bordered real BUTTON
 * with NO fill of its own, so the dialog's surface shows through (hover =
 * the shared wash + the brightened border), fluid in its grid cell. Height
 * is content-capped: the name and description each clamp at two lines, so
 * every card sits at the tight two-line mass with no filler band. No
 * footnote line — the name + description carry the card; how-to copy
 * belongs in the config panel. */
function MarketplaceCard({ card }: { card: CardModel }) {
  return (
    <Box
      as="button"
      direction="column"
      border
      rounded="lg"
      fill="width"
      gap="md"
      justify="between"
      padding="md"
      hoverBackground={card.onClick ? true : undefined}
      cursor={card.onClick ? "pointer" : undefined}
      className={CARD_CHROME}
      onClick={card.onClick}
    >
      <Box align="start" direction="row" justify="between" fill="width">
        <IconChip color={card.iconColor} logo={card.logo} name={card.icon} />
        {card.status ? (
          <Text size="xs" variant={card.status.color} weight="medium">
            {card.status.label}
          </Text>
        ) : null}
      </Box>
      <Box gap="2xs" fill="width">
        <Text weight="semibold" truncate={2}>
          {card.name}
        </Text>
        <CardDescription text={card.description} />
      </Box>
    </Box>
  );
}

/** First-load stand-in for the card grid: six tiles on the marketplace
 * card's own anatomy (icon chip, name, two description lines) so the real
 * cards land with no reflow. Bars are `delayed` — the catalog fetch starts
 * on open, and a fast response swaps cards in before any shimmer paints. */
function CardGridSkeleton() {
  return (
    <Box columns="fill" fill="width" gap="md">
      {Array.from({ length: 6 }, (_, index) => (
        <Box
          key={index}
          border
          direction="column"
          fill="width"
          gap="md"
          padding="md"
          rounded="lg"
        >
          <Skeleton delayed height={32} width={32} />
          <Box direction="column" fill="width" gap="xs">
            <Skeleton delayed height={14} width="60%" />
            <Skeleton delayed fillWidth height={10} />
            <Skeleton delayed height={10} width="80%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Card grid + empty state: the fluid `fill` grid, which counts columns off
 * the pane's OWN width — one in a squeezed window, four across the full
 * 1120px dialog — so a card never drops under the width its name needs. An
 * empty result distinguishes a fruitless SEARCH (offer to clear it) from a
 * category with nothing published in it yet. */
function CardGrid({
  cards,
  query = "",
  onClearSearch,
}: {
  cards: CardModel[];
  query?: string;
  onClearSearch?: () => void;
}) {
  if (cards.length === 0) {
    const searching = query.length > 0;
    return (
      <Box align="center" fill="width" paddingY="2xl">
        <EmptyState
          media="icon"
          icon={searching ? "search-x" : "layout-grid"}
          title={
            searching ? "No matching integrations" : "No integrations here yet"
          }
          description={
            searching
              ? `Nothing matches “${query}”. Try a different name or browse another category.`
              : "Integrations in this category will appear here as they become available."
          }
          actions={
            searching && onClearSearch ? (
              <Button size="sm" variant="outline" onClick={onClearSearch}>
                <Text size="sm">Clear search</Text>
              </Button>
            ) : undefined
          }
        />
      </Box>
    );
  }
  return (
    <Box columns="fill" fill="width" gap="md">
      {cards.map((card, index) => (
        <MarketplaceCard key={`${card.id}-${index}`} card={card} />
      ))}
    </Box>
  );
}

/** Left-nav: the category filters for the provider grid, then the
 * workspace's own feature sections — two unrelated genres, so a separator
 * sits between them rather than the group rhythm alone. */
function CategoryNav({
  categories,
  hasProviders,
  selectedId,
  onSelectCategory,
}: {
  categories: IntegrationCategory[];
  /** Whether any provider exists — renders nothing if not. */
  hasProviders: boolean;
  selectedId: string;
  onSelectCategory: (id: string) => void;
}) {
  return (
    <NavPaneContent>
      {hasProviders ? (
        <NavPaneGroup>
          <NavPaneGroupLabel>Integrations</NavPaneGroupLabel>
          {categories.map((category, index) => (
            <NavPaneItem
              key={`${category.id}-${index}`}
              isActive={category.id === selectedId}
              onClick={() => onSelectCategory(category.id)}
            >
              <Icon name={category.icon} />
              {category.label}
            </NavPaneItem>
          ))}
        </NavPaneGroup>
      ) : null}

      {hasProviders ? <NavPaneSeparator /> : null}

      <NavPaneGroup>
        <NavPaneGroupLabel>SalesTrak Apps</NavPaneGroupLabel>
        <NavPaneEmpty>Feature Flags Migrating Here</NavPaneEmpty>
      </NavPaneGroup>
    </NavPaneContent>
  );
}

/** Shared search input. */
function IntegrationSearch({
  query,
  inputRef,
  onChange,
}: {
  query: string;
  /** The dialog's `initialFocus` target — search takes focus on open. */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onChange: (q: string) => void;
}) {
  return (
    <InputGroup>
      <InputGroupAddon>
        <Icon name="search" />
      </InputGroupAddon>
      <InputGroupInput
        ref={inputRef}
        placeholder="Search..."
        type="search"
        value={query}
        onValueChange={onChange}
      />
    </InputGroup>
  );
}

/** Empty state when the marketplace has no providers. */
function ComingSoonPanel() {
  return (
    <Box align="center" fill="width" paddingY="2xl">
      <EmptyState
        media="icon"
        icon="package"
        title="Coming soon"
        description="Integrations will appear here once they're available."
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dialog — desktop two-pane (nav + card grid), mobile drawer (search + grid).
// ---------------------------------------------------------------------------

export function IntegrationsManageDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // Small desktop windows and iPads: the window dialog is viewport-bounded,
  // so the nav pane gives the card grid back some of its width.
  const isNarrow = useMediaQuery("max-md");
  // Open lands focus in the header search — type-to-filter immediately
  // (Spencer, 2026-08-21; same contract as the Add Widget gallery).
  const searchRef = React.useRef<HTMLInputElement>(null);
  // Marketplace data from the Core API (code registry); fetch once open.
  const { categories, providers, initialLoading } = useIntegrationCatalog({
    skip: !open,
  });
  const hasProviders = providers.length > 0;
  const isEmpty = !initialLoading && !hasProviders;
  const [selectedId, setSelectedId] = React.useState(DEFAULT_CATEGORY_ID);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<IntegrationProvider | null>(null);
  // Which of the selected provider's connections the panel has open (or
  // NEW_CONNECTION). Lifted out of `ConnectionConfigView` so the breadcrumb owns
  // that level of navigation instead of an in-panel back link. `null` = the
  // provider's connections list.
  const [connectionId, setConnectionId] = React.useState<string | null>(null);
  // `selected` holds a snapshot of the provider at open time; re-resolve the
  // live one from the (re-fetched) catalog so edits — rename, status, delete —
  // reflect immediately without reopening. Falls back to the snapshot.
  const selectedProvider = selected
    ? providers.find((p) => p.id === selected.id) ?? selected
    : null;
  // Content pane width (ResizeObserver) — sizes the fixed-card grid columns and
  // the centered config column.
  const { ref: paneRef, size: paneSize } = useElementSize<HTMLDivElement>();
  const paneWidth = Math.floor(paneSize.width);

  // Leaving the config panel always drops the open connection with it, so the
  // next provider never inherits the previous one's detail level.
  const clearSelection = React.useCallback(() => {
    setSelected(null);
    setConnectionId(null);
  }, []);

  React.useEffect(() => {
    if (!open) {
      setSelectedId(DEFAULT_CATEGORY_ID);
      setQuery("");
      clearSelection();
    }
  }, [open, clearSelection]);

  const handleSelectCategory = React.useCallback(
    (id: string) => {
      setSelectedId(id);
      clearSelection();
    },
    [clearSelection],
  );

  // Clicking a provider card opens the config panel.
  const handleProviderAction = React.useCallback((p: IntegrationProvider) => {
    setSelected(p);
    setConnectionId(null);
  }, []);

  // Page segment for the open connection — null while the connections list is
  // showing, and also when the open connection has just left the catalog (a
  // delete), which `ConnectionConfigView` resolves by resetting to the list.
  // A persisted connection's detail view is the one panel that manages its own
  // scrolling (fixed header + tab strip, scrolling tab content), so the desktop
  // pane hands it a bounded height instead of wrapping it in a page-level
  // scroll area. The connections list and the new-connection form scroll as a
  // page like every other panel.
  const connectionDetailOpen =
    selectedProvider !== null &&
    connectionId !== null &&
    connectionId !== NEW_CONNECTION;

  const connectionCrumb: ConfigCrumbSegment | null = React.useMemo(() => {
    if (connectionId === null) return null;
    if (connectionId === NEW_CONNECTION) return { label: "New connection", icon: "plus" };
    const name = selectedProvider?.connections.find((c) => c.id === connectionId)?.name;
    return name ? { label: name, icon: "plug" } : null;
  }, [connectionId, selectedProvider]);

  // Trail for the provider panel: the grid the card was opened from → the
  // provider → the open connection. The provider segment is a link only while a
  // connection is open, since that is the only time it navigates anywhere.
  const providerTrail = (
    provider: IntegrationProvider,
    rootLabel: string,
    rootIcon: IconName,
  ): ConfigCrumbSegment[] => [
    { label: rootLabel, icon: rootIcon, onClick: clearSelection },
    {
      label: provider.name,
      icon: provider.icon,
      logo: provider.logo,
      onClick: connectionCrumb ? () => setConnectionId(null) : undefined,
    },
    ...(connectionCrumb ? [connectionCrumb] : []),
  ];

  // The same switch the Dialog presents its sheet on.
  const isMobile = useIsMobile();
  const mobileCards = providers.map((p) => providerCard(p, handleProviderAction));
  const mobileVisible = mobileCards.filter((c) => matchesQuery(c, query));

  // A category with no providers in it gets no nav row and no grid — empty
  // buckets are noise, not navigation. When the selected category is hidden
  // (or the server omitted Featured), fall back to All Integrations, else
  // the first visible bucket, so the pane never shows an empty grid.
  const visibleCategories = categories.filter(
    (c) => filterProviders(c.id, providers).length > 0,
  );
  const effectiveSelectedId = visibleCategories.some((c) => c.id === selectedId)
    ? selectedId
    : visibleCategories.some((c) => c.id === "all-integrations")
      ? "all-integrations"
      : visibleCategories[0]?.id ?? selectedId;
  const activeCategory = visibleCategories.find(
    (c) => c.id === effectiveSelectedId,
  );
  const activeLabel = activeCategory?.label ?? "Featured";
  const crumbIcon: IconName = activeCategory?.icon ?? "layout-grid";
  const cards = filterProviders(effectiveSelectedId, providers).map((p) =>
    providerCard(p, handleProviderAction),
  );
  const visible = cards.filter((c) => matchesQuery(c, query));

  const configWidth = paneWidth
    ? Math.min(CONFIG_MAX_WIDTH, paneWidth - CONTENT_PAD_X * 2)
    : undefined;
  // The content column, rendered inside the page scroll area normally and
  // standalone (bounded, so the panel scrolls internally) for a connection
  // detail. In that mode the pane also drops its own horizontal padding and
  // centering — the panel centers each of its regions instead, so its scroll
  // container spans the full pane and its scrollbar lands on the pane edge.
  const pane = (
    <Box
      ref={paneRef}
      align={connectionDetailOpen ? undefined : "center"}
      // A flex CONTAINER, which is a separate thing from the flex ITEM `fill`
      // makes this. The detail panel bounds itself by resolving `flex-1` against
      // this Box, and only a flex parent offers that to resolve against — a bare
      // Box is `display: block`, which hands the panel an auto height that grows
      // to its content, and the tab body then has no height to scroll within.
      // `align` opts in on its own for the connections list; the detail branch
      // passes none, so the mode that needs the chain is the one that loses it.
      direction="column"
      fill={connectionDetailOpen ? true : "width"}
      // Flex children default to `min-height: auto`; without this the column
      // grows to its content instead of bounding the panel inside it.
      minHeight={connectionDetailOpen ? 0 : undefined}
      paddingBottom={connectionDetailOpen ? undefined : "md"}
      paddingTop="md"
      paddingX={connectionDetailOpen ? undefined : "lg"}
    >
      {connectionDetailOpen && selectedProvider ? (
        <ConnectionConfigView
          activeConnectionId={connectionId}
          contentWidth={configWidth}
          fillHeight
          provider={selectedProvider}
          onActiveConnectionIdChange={setConnectionId}
        />
      ) : selectedProvider ? (
        <Box fill={configWidth ? undefined : "width"} width={configWidth}>
          <ConnectionConfigView
            activeConnectionId={connectionId}
            provider={selectedProvider}
            onActiveConnectionIdChange={setConnectionId}
          />
        </Box>
      ) : initialLoading ? (
        <CardGridSkeleton />
      ) : isEmpty ? (
        <ComingSoonPanel />
      ) : (
        <Box fill="width">
          {/* No pane heading — the content header already names the active
              category. */}
          <CardGrid
            cards={visible}
            query={query}
            onClearSearch={() => setQuery("")}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* The window tier, like keyboard shortcuts and custom fields: browsing
       * the catalog is a bounded window over the app, not a takeover. */}
      <DialogPopup
        initialFocus={searchRef}
        showCloseButton={false}
        size="window"
        // The manage-dialog room rests on the page fill: its nav pane lifts
        // above it and its tables read on the surface they read on everywhere.
        surface="background"
      >
        {/* Phone: search and the card grid, no nav pane. */}
        {isMobile ? (
          <>
            <DialogHeader>
              <DialogTitle>App Center</DialogTitle>
            </DialogHeader>
            {selectedProvider ? (
              <Box paddingBottom="sm" paddingX="md">
                <ConfigCrumb
                  trail={providerTrail(selectedProvider, "All Integrations", "layout-grid")}
                />
              </Box>
            ) : isEmpty ? null : (
              <Box paddingBottom="sm" paddingX="md">
                <IntegrationSearch query={query} onChange={setQuery} />
              </Box>
            )}

            <DialogPanel>
              <Box gap="md">
                {selectedProvider ? (
                  <ConnectionConfigView
                    activeConnectionId={connectionId}
                    provider={selectedProvider}
                    onActiveConnectionIdChange={setConnectionId}
                  />
                ) : isEmpty ? (
                  <ComingSoonPanel />
                ) : initialLoading ? (
                  <CardGridSkeleton />
                ) : (
                  <CardGrid
                    cards={mobileVisible}
                    query={query}
                    onClearSearch={() => setQuery("")}
                  />
                )}
              </Box>
            </DialogPanel>
          </>
        ) : (
          <>
            {/* Screens carry their identity in the shared header; this satisfies
                the accessible-name requirement on the dialog popup. */}
            <DialogTitle visuallyHidden>App Center</DialogTitle>
            <Box direction="row" fill minHeight={0}>
              {/* Left nav — full height; it owns the dialog identity (tile +
                  title) at the top, aligned on the content header's 48px rung. */}
              <NavPane width={isNarrow ? NAV_WIDTH_NARROW : NAV_WIDTH}>
                <NavPaneHeader>
                  {/* Neutral ink on purpose — an identity mark, not a themed
                      accent. */}
                  <IconTile bordered name="puzzle" size="lg" />
                  <NavPaneTitle>App Center</NavPaneTitle>
                </NavPaneHeader>
                <CategoryNav
                  categories={visibleCategories}
                  hasProviders={hasProviders}
                  selectedId={effectiveSelectedId}
                  onSelectCategory={handleSelectCategory}
                />
              </NavPane>

              {/* Content */}
              <Box direction="column" fill minWidth={0}>
                {/* The content header names WHERE you are: the active category
                    while browsing (the identity lives in the sidebar), or the
                    crumb trail once a provider is open. Search sits against the
                    close pill while browsing the grid. */}
                <ManageDialogHeader onClose={onClose}>
                  {/* The crumb GROWS from its intrinsic width, so the free space
                      lands between it and the search — the field stays pinned
                      against the close at its cap. Squeezed, the field shrinks
                      first: while it is present the crumb refuses to shrink (a
                      category label is short; the field has a floor to give).
                      With the field gone — a provider open, a long trail — the
                      crumb shrinks and truncates instead of running under the
                      close. */}
                  <Box
                    align="center"
                    direction="row"
                    gap="sm"
                    grow
                    minWidth={0}
                    shrink={selectedProvider ? undefined : false}
                  >
                    {/* One ConfigCrumb at both levels: browsing renders the
                        category as a lone inert segment on the SAME chip
                        geometry the drilled trail's root link uses, so opening a
                        provider only appends "/ Provider" — nothing shifts or
                        restyles. */}
                    <ConfigCrumb
                      trail={
                        selectedProvider
                          ? providerTrail(selectedProvider, activeLabel, crumbIcon)
                          : [{ label: activeLabel, icon: crumbIcon }]
                      }
                    />
                  </Box>
                  {selectedProvider ? null : (
                    <Box
                      minWidth={SEARCH_MIN_WIDTH}
                      shrink
                      width={SEARCH_MAX_WIDTH}
                    >
                      <IntegrationSearch
                        query={query}
                        inputRef={searchRef}
                        onChange={setQuery}
                      />
                    </Box>
                  )}
                </ManageDialogHeader>
                {connectionDetailOpen ? pane : <ScrollArea>{pane}</ScrollArea>}
              </Box>
            </Box>
      
          </>
        )}
      </DialogPopup>
    </Dialog>
  );
}
